import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ProcessoSheet } from "../components/ProcessoSheet";
import { RelatorioDialog } from "../components/RelatorioDialog";
import { DuplicadosDialog } from "../components/DuplicadosDialog";
import { TrocarSenhaDialog } from "../components/TrocarSenhaDialog";
import { MovimentacaoDiaria } from "../components/MovimentacaoDiaria";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  ShieldCheck, Plus, Search, LogOut, ChevronLeft, ChevronRight, FileText,
  FolderOpen, CheckCircle2, Clock, Gavel, Loader2, Download, KeyRound, ChevronDown,
  AlertTriangle, BarChart3, Printer, Building2, CopyCheck, Settings,
} from "lucide-react";

const statusClass = (s = "") => {
  const v = (s || "").toUpperCase();
  if (v.includes("POSS") && !v.includes("IMPOSS")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (v.includes("IMPOSS")) return "bg-red-50 text-red-700 border-red-200";
  if (v.includes("CONCL") || v.includes("FINAL") || v.includes("HOMOLOG")) return "bg-blue-50 text-blue-700 border-blue-200";
  if (v.includes("ANDAM") || v.includes("ANALIS")) return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const diasParados = (p) => {
  if (!p.data_entrada || p.data_conclusao) return null;
  return Math.floor((Date.now() - new Date(p.data_entrada).getTime()) / 86400000);
};

export default function Dashboard({ onOpenMaster }) {
  const { user, logout } = useAuth();
  const [tipo, setTipo] = useState("PROCESSO");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [origem, setOrigem] = useState("");
  const [ano, setAno] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [opts, setOpts] = useState({ status: [], origem: [], ano: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState({ open: false, processo: null });
  const [atrasados, setAtrasados] = useState(false);
  const [mensal, setMensal] = useState(null);
  const [anoMensal, setAnoMensal] = useState("");
  const [exporting, setExporting] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [relatorio, setRelatorio] = useState(false);
  const [duplicados, setDuplicados] = useState(false);
  const [situacao, setSituacao] = useState("");
  const [mes, setMes] = useState(null);
  const [trocarSenha, setTrocarSenha] = useState(false);
  const [dia, setDia] = useState(null);
  const [classificacao, setClassificacao] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const limit = 25;
  const isMaster = user?.role === "master";

  useEffect(() => {
    const t = setTimeout(() => { setSearch(q); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/processos", {
        params: {
          tipo_registro: tipo, page, limit,
          q: search || undefined, status: status || undefined,
          origem: origem || undefined, ano: ano ? Number(ano) : undefined,
          atrasados: atrasados || undefined,
          situacao: situacao || undefined,
          mes: mes || undefined,
          dia: dia || undefined,
          classificacao: classificacao || undefined,
        },
      });
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [tipo, page, search, status, origem, ano, atrasados, situacao, mes, dia, classificacao]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/processos/options", { params: { tipo_registro: tipo } }).then(({ data }) => setOpts(data));
    setStatus(""); setOrigem(""); setAno(""); setPage(1);
  }, [tipo]);  useEffect(() => { api.get("/stats").then(({ data }) => setStats(data)); }, [data.total, refreshKey]);

  useEffect(() => {
    api.get("/stats/mensal", { params: { ano: anoMensal ? Number(anoMensal) : undefined } })
      .then(({ data }) => { setMensal(data); if (!anoMensal) setAnoMensal(String(data.ano)); });
  }, [anoMensal, refreshKey]);

  useEffect(() => {
    api.get("/stats/unidades", { params: { ano: anoMensal ? Number(anoMensal) : undefined } })
      .then(({ data }) => setUnidades(data));
  }, [anoMensal, data.total, refreshKey]);

  const MESES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

  const aplicarMes = (nomeMes) => {
    const idx = MESES.indexOf(nomeMes) + 1;
    setPage(1);
    setTipo("PROCESSO");
    setSituacao("");
    setAtrasados(false);
    setDia(null);
    setStatus(""); setOrigem(""); setQ("");
    setAno(String(mensal?.ano || anoMensal));
    setMes(mes === idx ? null : idx);
    document.getElementById("tabela-processos")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const aplicarDia = (dataISO) => {
    setPage(1);
    setSituacao(""); setAtrasados(false); setMes(null);
    setStatus(""); setOrigem(""); setQ(""); setAno("");
    setDia(dataISO);
    if (dataISO) {
      document.getElementById("tabela-processos")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const exportar = async () => {
    setExporting(true);
    try {
      const res = await api.get("/processos/export", {
        params: {
          tipo_registro: tipo, q: search || undefined, status: status || undefined,
          origem: origem || undefined, ano: ano ? Number(ano) : undefined,
          mes: mes || undefined,
          dia: dia || undefined,
          classificacao: classificacao || undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `afs-track-processos-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const pages = Math.max(1, Math.ceil(data.total / limit));
  const aplicarKpi = (key) => {
    setPage(1);
    setStatus(""); setOrigem(""); setQ(""); setMes(null); setDia(null);
    if (key === "total") { setTipo("PROCESSO"); setSituacao(""); setAtrasados(false); }
    if (key === "concluidos") { setTipo("PROCESSO"); setSituacao("concluidos"); setAtrasados(false); }
    if (key === "andamento") { setTipo("PROCESSO"); setSituacao("andamento"); setAtrasados(false); }
    if (key === "atrasados") { setTipo("PROCESSO"); setSituacao(""); setAtrasados(true); }
    if (key === "sindicancias") { setTipo("SINDICANCIA"); setSituacao(""); setAtrasados(false); }
    document.getElementById("tabela-processos")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const kpis = useMemo(() => stats ? [
    { key: "total", label: "Processos", value: stats.total, icon: FolderOpen },
    { key: "concluidos", label: "Concluídos", value: stats.concluidos, icon: CheckCircle2 },
    { key: "andamento", label: "Em andamento", value: stats.em_andamento, icon: Clock },
    { key: "atrasados", label: `Atrasados (+${stats.alerta_dias}d)`, value: stats.atrasados, icon: AlertTriangle, alert: true },
    { key: "sindicancias", label: "Sindicâncias", value: stats.sindicancias, icon: Gavel },
  ] : [], [stats]);

  const kpiAtivo = atrasados
    ? "atrasados"
    : tipo === "SINDICANCIA"
    ? "sindicancias"
    : situacao || "total";

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <header className="border-b border-border bg-white sticky top-0 z-30">
        <div className="px-4 sm:px-6 h-16 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-primary grid place-items-center">
              <ShieldCheck className="h-4.5 w-4.5 text-white" />
            </div>
            <div className="leading-tight">
              <p className="text-[10px] tracking-[0.22em] uppercase text-slate-400">e-Salvador</p>
              <p className="text-sm font-semibold font-display tracking-tight">Controle de Processos</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={onOpenMaster}
              data-testid="open-master-btn"
              title="Configurações do administrador"
              aria-label="Configurações do administrador"
              className="h-9 w-9 grid place-items-center text-slate-500 hover:text-primary hover:bg-slate-50 border border-border rounded-md transition-colors duration-150"
            >
              <Settings className="h-4 w-4" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 transition-colors duration-150"
                >
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium leading-tight">{user?.name}</p>
                    <p className="text-[11px] text-slate-500 mono">{user?.username} · {user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">
                  {user?.name}
                  <span className="block text-[11px] font-normal text-slate-500 mono">{user?.username}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTrocarSenha(true)} data-testid="menu-trocar-senha">
                  <KeyRound className="h-4 w-4 mr-2" /> Trocar minha senha
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onOpenMaster} data-testid="menu-admin">
                  <Settings className="h-4 w-4 mr-2" /> Painel do administrador
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-sair" className="text-red-700">
                  <LogOut className="h-4 w-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-6 max-w-[1700px] mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6 fade-up">
          {kpis.map((k) => (
            <button
              key={k.label}
              onClick={() => aplicarKpi(k.key)}
              data-testid={`kpi-${k.key}`}
              className={`text-left border p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                k.alert && k.value > 0 ? "border-red-200 bg-red-50" : "border-border bg-white"
              } ${kpiAtivo === k.key ? "ring-2 ring-primary ring-offset-1" : ""}`}
            >
              <div className="flex items-start justify-between">
                <p className={`text-[11px] uppercase tracking-wider ${k.alert && k.value > 0 ? "text-red-700" : "text-slate-500"}`}>{k.label}</p>
                <k.icon className={`h-4 w-4 ${k.alert && k.value > 0 ? "text-red-400" : "text-slate-300"}`} />
              </div>
              <p className={`text-3xl font-semibold font-display mt-2 tracking-tight ${k.alert && k.value > 0 ? "text-red-800" : ""}`}>
                {k.value?.toLocaleString("pt-BR")}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">ver registros</p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-1 gap-4 mb-6">
          {mensal && (
            <div className="border border-border bg-white p-4" data-testid="monthly-chart">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-slate-400" />
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">
                    Processos tramitados por mês
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 mono">
                    Total {mensal.total.toLocaleString("pt-BR")}
                  </span>
                  <select
                    value={anoMensal}
                    onChange={(e) => setAnoMensal(e.target.value)}
                    data-testid="monthly-year-filter"
                    className="h-8 border border-input bg-white px-2 text-xs rounded-md mono"
                  >
                    {(mensal.anos || []).map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-end gap-1.5 h-40">
                {mensal.meses.map((m) => {
                  const max = Math.max(1, ...mensal.meses.map((x) => x.count));
                  const h = Math.round((m.count / max) * 100);
                  const ativo = mes === MESES.indexOf(m.mes) + 1;
                  return (
                    <button
                      key={m.mes}
                      onClick={() => aplicarMes(m.mes)}
                      data-testid={`monthly-bar-${m.mes}`}
                      title={`${m.mes}: ${m.count} — clique para ver os processos`}
                      aria-pressed={ativo}
                      className="flex-1 flex flex-col items-center justify-end h-full group cursor-pointer"
                    >
                      <span className={`text-[10px] mono mb-1 transition-opacity duration-150 ${
                        ativo ? "text-primary opacity-100" : "text-slate-500 opacity-0 group-hover:opacity-100"
                      }`}>
                        {m.count}
                      </span>
                      <div
                        className={`w-full transition-all duration-300 ${
                          ativo ? "bg-primary ring-2 ring-primary/40" : "bg-primary/85 group-hover:bg-primary"
                        }`}
                        style={{ height: `${Math.max(h, m.count ? 3 : 0)}%` }}
                      />
                      <span className={`text-[9px] mono mt-1.5 ${ativo ? "text-primary font-medium" : "text-slate-400"}`}>
                        {m.mes}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">Clique em um mês para ver os processos</p>
            </div>
          )}
        </div>

        <MovimentacaoDiaria
          tipoRegistro={tipo}
          diaSelecionado={dia}
          onSelecionarDia={aplicarDia}
          refreshKey={refreshKey}
        />

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex border border-border bg-white">
            {[["PROCESSO", "Processos"], ["SINDICANCIA", "Sindicâncias"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setTipo(v)}
                data-testid={`tab-${v}`}
                className={`px-4 h-9 text-sm transition-colors duration-150 ${
                  tipo === v ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar nº, requerente, assunto…"
              data-testid="search-input"
              className="h-9 pl-9 w-full sm:w-80 text-sm"
            />
          </div>

          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            data-testid="filter-status" className="h-9 border border-input bg-white px-2 text-sm rounded-md max-w-[190px]">
            <option value="">Todos os status</option>
            {opts.status.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={origem} onChange={(e) => { setOrigem(e.target.value); setPage(1); }}
            data-testid="filter-origem" className="h-9 border border-input bg-white px-2 text-sm rounded-md max-w-[170px]">
            <option value="">Todas as origens</option>
            {["EXTERNO", "INTERNO", "E-MAIL", "OUTROS"].map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          <select value={ano} onChange={(e) => { setAno(e.target.value); setPage(1); }}
            data-testid="filter-ano" className="h-9 border border-input bg-white px-2 text-sm rounded-md">
            <option value="">Todos os anos</option>
            {opts.ano.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>

          <div className="flex border border-border bg-white" data-testid="classificacao-filtros">
            {[["", "Todas"], ["SINDICÂNCIA", "Sindicância"], ["PAD", "PAD"], ["LICITAÇÃO", "Licitação"]].map(([v, l]) => (
              <button
                key={l}
                onClick={() => { setClassificacao(v); setPage(1); }}
                data-testid={`filter-classificacao-${v || "todas"}`}
                className={`px-3 h-9 text-sm transition-colors duration-150 ${
                  classificacao === v ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <button
            onClick={() => { setAtrasados((v) => !v); setPage(1); }}            data-testid="filter-atrasados"
            className={`h-9 px-3 text-sm border rounded-md flex items-center gap-2 transition-colors duration-150 ${
              atrasados
                ? "bg-red-600 text-white border-red-600"
                : "bg-white border-input text-slate-600 hover:bg-red-50"
            }`}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Atrasados
          </button>

          <Button
            variant="outline"
            onClick={() => setDuplicados(true)}
            data-testid="open-duplicados-btn"
            className="h-9"
          >
            <CopyCheck className="h-4 w-4 mr-2" /> Duplicidades
          </Button>

          <div className="flex items-center gap-2 ml-auto">
            {isMaster && (
              <Button variant="outline" onClick={() => setRelatorio(true)} data-testid="open-report-btn" className="h-9">
                <Printer className="h-4 w-4 mr-2" /> Relatório
              </Button>
            )}
            <Button
              variant="outline"
              onClick={exportar}
              disabled={exporting}
              data-testid="export-excel-btn"
              className="h-9"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {exporting ? "" : "Exportar Excel"}
            </Button>
            <Button
              onClick={() => setSheet({ open: true, processo: null })}
              data-testid="new-processo-btn"
              className="h-9 bg-primary hover:bg-[#003366]"
            >
              <Plus className="h-4 w-4 mr-2" /> Novo registro
            </Button>
          </div>
        </div>

        <div id="tabela-processos" className="border border-border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-border">
            <p className="text-xs text-slate-500">
              <span className="mono font-medium text-slate-800">{data.total.toLocaleString("pt-BR")}</span> registro(s)
              {situacao === "concluidos" && <span className="ml-2 text-blue-700">· concluídos</span>}
              {situacao === "andamento" && <span className="ml-2 text-amber-700">· em andamento</span>}
              {mes && (
                <span className="ml-2 text-primary font-medium" data-testid="mes-chip">
                  · {MESES[mes - 1]}/{ano || mensal?.ano}
                </span>
              )}
              {dia && (
                <span className="ml-2 text-primary font-medium" data-testid="dia-chip">
                  · movimentados em {dia.split("-").reverse().join("/")}
                </span>
              )}
              {atrasados && <span className="ml-2 text-red-700">· atrasados</span>}
            </p>
            <div className="flex items-center gap-3">
              {(situacao || atrasados || mes || dia) && (
                <button
                  onClick={() => { setSituacao(""); setAtrasados(false); setMes(null); setDia(null); setPage(1); }}
                  data-testid="clear-situacao-btn"
                  className="text-xs text-slate-500 hover:text-primary underline transition-colors duration-150"
                >
                  limpar seleção
                </button>
              )}
              {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" data-testid="table-loading" />}
            </div>
          </div>
          <div className="overflow-x-auto table-scroll">
            <table className="w-full text-sm min-w-[1100px]" data-testid="processos-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5">Nº Processo</th>
                  <th className="px-3 py-2.5">Ano</th>
                  <th className="px-3 py-2.5">Entrada</th>
                  <th className="px-3 py-2.5">Origem</th>
                  <th className="px-3 py-2.5">Requerente</th>
                  <th className="px-3 py-2.5">Unid. destino</th>
                  <th className="px-3 py-2.5">Assunto</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Conclusão</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => {
                  const dias = diasParados(p);
                  const late = dias !== null && dias >= (stats?.alerta_dias ?? 30);
                  return (
                  <tr
                    key={p._id}
                    onClick={() => setSheet({ open: true, processo: p })}
                    data-testid={`processo-row-${p._id}`}
                    data-late={late ? "true" : "false"}
                    className={`border-t border-border cursor-pointer transition-colors duration-150 ${
                      late ? "bg-red-50/70 hover:bg-red-100/70" : "hover:bg-slate-50"
                    }`}
                  >
                    <td className={`px-3 py-2 mono whitespace-nowrap font-medium ${late ? "text-red-800" : ""}`}>
                      {late && <AlertTriangle className="h-3 w-3 inline mr-1.5 -mt-0.5 text-red-600" />}
                      {p.numero_processo || "—"}
                    </td>
                    <td className="px-3 py-2 mono text-xs">{p.ano || "—"}</td>
                    <td className="px-3 py-2 mono text-xs whitespace-nowrap">
                      {fmtDate(p.data_entrada)}
                      {late && <span className="ml-1.5 text-red-600">{dias}d</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">{p.origem || "—"}</td>
                    <td className="px-3 py-2 max-w-[200px] truncate">{p.nome_requerente || "—"}</td>
                    <td className="px-3 py-2 text-xs max-w-[160px] truncate">{p.unidade_destino || "—"}</td>
                    <td className="px-3 py-2 max-w-[320px] truncate text-slate-600">{p.assunto || "—"}</td>
                    <td className="px-3 py-2">
                      {p.status ? (
                        <Badge variant="outline" className={`${statusClass(p.status)} text-[10px]`}>{p.status}</Badge>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 mono text-xs whitespace-nowrap">{fmtDate(p.data_conclusao)}</td>
                  </tr>
                  );
                })}
                {!loading && data.items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-16 text-center">
                      <FileText className="h-8 w-8 mx-auto text-slate-200 mb-2" />
                      <p className="text-slate-400 text-sm">Nenhum registro encontrado</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-xs text-slate-500 mono">Página {page} de {pages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)} data-testid="prev-page-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)} data-testid="next-page-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      <ProcessoSheet
        open={sheet.open}
        onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))}
        processo={sheet.processo}
        tipoRegistro={tipo}
        alertaDias={stats?.alerta_dias ?? 30}
        onSaved={() => { load(); setRefreshKey((k) => k + 1); }}
      />

      <RelatorioDialog
        open={relatorio}
        onOpenChange={setRelatorio}
        stats={stats}
        mensal={mensal}
        unidades={unidades}        ano={anoMensal}
        usuario={`${user?.name} (${user?.username})`}
      />

      <DuplicadosDialog
        open={duplicados}
        onOpenChange={setDuplicados}
        tipoRegistro={tipo}
        ano={ano}
      />

      <TrocarSenhaDialog open={trocarSenha} onOpenChange={setTrocarSenha} />
    </div>
  );
}
